import type { ReactNode } from "react";
import { Navigate, Route, Routes } from "react-router-dom";
import { LoginScreen } from "./access/LoginScreen";
import { RecoverPasswordScreen } from "./access/RecoverPasswordScreen";
import { NewPasswordScreen } from "./access/NewPasswordScreen";
import { loadValidatorSession } from "./access/session";
import { HomeScreen } from "./home/HomeScreen";
import { ScannerScreen } from "./scanner/ScannerScreen";
import { ConfirmScreen } from "./confirm/ConfirmScreen";
import { ResultScreen } from "./result/ResultScreen";

// SPEC-301: tres pantallas de acceso del validador. `web-core` monta este
// componente bajo `<Route path="/validator/*">` en `main.tsx`, así que las
// rutas de abajo son relativas — no repiten el prefijo `/validator`.
//
// Deliberadamente mínimo (web/AGENTS.md, rol web-validator): cinco vistas no
// justifican un sistema de rutas propio ni una capa de layout compartida
// entre superficies. `<Routes>` plano alcanza.
function hasValidSession(): boolean {
  const session = loadValidatorSession();
  if (!session) return false;
  return new Date(session.expiresAt).getTime() > Date.now();
}

// SPEC-302: guardia de sesión para `home`/`scan`/`confirm`. Un componente
// simple, no un router propio — cinco pantallas no pagan esa abstracción.
function RequireValidatorSession({ children }: { children: ReactNode }) {
  if (!hasValidSession()) {
    return <Navigate to="/validator/access-login" replace />;
  }
  return <>{children}</>;
}

export function ValidatorApp() {
  return (
    <Routes>
      <Route path="access-login" element={<LoginScreen />} />
      <Route path="access-recover" element={<RecoverPasswordScreen />} />
      <Route path="access-new-password" element={<NewPasswordScreen />} />
      <Route
        path="home"
        element={
          <RequireValidatorSession>
            <HomeScreen />
          </RequireValidatorSession>
        }
      />
      <Route
        path="scan"
        element={
          <RequireValidatorSession>
            <ScannerScreen />
          </RequireValidatorSession>
        }
      />
      <Route
        path="confirm"
        element={
          <RequireValidatorSession>
            <ConfirmScreen />
          </RequireValidatorSession>
        }
      />
      <Route
        path="result"
        element={
          <RequireValidatorSession>
            <ResultScreen />
          </RequireValidatorSession>
        }
      />
      <Route
        path="*"
        element={
          hasValidSession() ? (
            <Navigate to="home" replace />
          ) : (
            <Navigate to="access-login" replace />
          )
        }
      />
    </Routes>
  );
}
